import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AccountDataContext } from './AccountContext';
import { API_URL } from '../config/env';
import { createBearerJsonClient } from '../utils/apiFetchPolicy';
const FavoritesContext = createContext();


export const useAccountData = () => {
    const context = useContext(AccountDataContext);
    if (!context) {
        console.error('useAccountData must be used within an AccountDataProvider');
        // Return a default structure to prevent crashes during development
        return {
            setAuth: () => {},
            setToken: () => {},
            auth: null,
            token: null,
            email: null,
            password: null,
            addresses: [],
            setAddresses: () => {},
            paymentMethods: [],
            setPaymentMethods: () => {},
            accountDetails: null,
            setAccountDetails: () => {},
            fetchAccountData: () => {},
            isLoading: false,
        };
    }
    return context;
};

export const useFavorites = () => {
    const context = useContext(FavoritesContext);
    if (!context) {
        throw new Error('useFavorites must be used within a FavoritesProvider');
    }
    return context;
};

export const FavoritesProvider = ({ children }) => {

    const { auth, token } = useAccountData();
    const user = auth;
    const [favorites, setFavorites] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const getApi = (authToken) => {
        return createBearerJsonClient(authToken, { baseURL: API_URL });
    };

    // Load favorites from backend or localStorage
    const loadFavorites = useCallback(async (userId, authToken) => {
       if (userId && authToken) {
            // Load from backend
            setIsLoading(true);
            try {
                const api = getApi(authToken);
                const response = await api.get(`/auth/favorites/${userId}`);
                if (response.data && response.data.favorites) {
                    
                    const favorites_item = response.data.favorites;
                    const itemIds = Array.isArray(favorites_item.itemIds) ? favorites_item.itemIds : [];
                    const favAt = Array.isArray(favorites_item.FavAt) ? favorites_item.FavAt : [];
                   
                    const favorites_item_new = itemIds.map((id, i) => ({
                        id,
                        favoritedAt: favAt[i]
                    }));

                    setFavorites(favorites_item_new);
                    // Also sync to localStorage as backup
                    localStorage.setItem('favorites', JSON.stringify(favorites_item_new));
                }
            } catch (error) {
                console.error('Error loading favorites from backend:', error);
                // Fallback to localStorage
                const savedFavorites = localStorage.getItem('favorites');
                if (savedFavorites) {
                    try {
                        setFavorites(JSON.parse(savedFavorites));
                    } catch (e) {
                        console.error('Error loading favorites from localStorage:', e);
                    }
                }
            } finally {
                setIsLoading(false);
            }
        } else {
            // Load from localStorage when not logged in
            const savedFavorites = localStorage.getItem('favorites');
            localStorage.removeItem('favorites');
            if (savedFavorites) {
                try {
                    setFavorites(JSON.parse(savedFavorites));
                } catch (error) {
                    console.error('Error loading favorites:', error);
                }
            }
        }
    }, []); // Empty dependency array since all dependencies are passed as parameters or are stable state setters

    // Load favorites on mount and when user changes
    useEffect(() => {
        const userId = user?._id || user?.id;
        loadFavorites(userId, token);
    }, [user, token, loadFavorites]);

    // Save favorites to backend when logged in, localStorage otherwise
    const saveFavorites = async (newFavorites, userId, authToken) => {
        // Always save to localStorage as backup
        localStorage.setItem('favorites', JSON.stringify(newFavorites));
        const favorites_id = newFavorites.map(f=>{
            return {id: f.id, favoritedAt: f.favoritedAt};
        });
        // Save to backend if logged in
        if (userId && authToken) {
            try {
                const api = getApi(authToken);
                await api.post(`/auth/favorites/${userId}`, {
                    favorites: favorites_id
                });

            } catch (error) {
                console.error('Error saving favorites to backend:', error);
                // Continue even if backend save fails - localStorage is saved
            }
        }
    };

    const addToFavorites = async (item) => {
        const userId = user?._id || user?.id;
        setFavorites(prevFavorites => {
            const existingItem = prevFavorites.find(favItem => favItem.id === item.id);
            if (!existingItem) {
                const newFavorites = [...prevFavorites, { id: item.id, favoritedAt: new Date().toISOString() }];
                saveFavorites(newFavorites, userId, token);
                return newFavorites;
            }
            return prevFavorites;
        });
    };

    const removeFromFavorites = async (itemId) => {
        const userId = user?._id || user?.id;
        setFavorites(prevFavorites => {
            const newFavorites = prevFavorites.filter(item => item.id !== itemId);
            saveFavorites(newFavorites, userId, token);
            return newFavorites;
        });
    };

    const isFavorite = (itemId) => {
        return favorites.some(item => item.id === itemId);
    };

    const toggleFavorite = async (item) => {
        if (isFavorite(item.id)) {
            await removeFromFavorites(item.id);
        } else {
            await addToFavorites(item);
        }
    };

    const clearFavorites = async () => {
        const userId = user?._id || user?.id;
        setFavorites([]);
        await saveFavorites([], userId, token);
    };

    return (
        <FavoritesContext.Provider value={{
            favorites,
            addToFavorites,
            removeFromFavorites,
            isFavorite,
            toggleFavorite,
            clearFavorites,
            isLoading,
            loadFavorites
        }}>
            {children}
        </FavoritesContext.Provider>
    );
};
